"use client";

import {
  Image as HeroUIImage,
  ImageProps as HeroUIImageProps,
} from "@heroui/image";
import NextImage from "next/image";
import { forwardRef, useState, useEffect } from "react";
import { useTranslations } from "next-intl";

export interface SEOImageProps extends Omit<HeroUIImageProps, "alt"> {
  src: string;
  alt: string;

  // SEO-specific props
  title?: string;
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "sync" | "async" | "auto";

  // Advanced image optimization
  sizes?: string;
  srcSet?: string;
  webpSrc?: string;
  avifSrc?: string;
  _placeholderSrc?: string;

  // Analytics and performance
  trackLoading?: boolean;
  trackError?: boolean;
  trackingCategory?: string;

  // Responsive image props
  responsive?: boolean;
  breakpoints?: Array<{
    maxWidth: number;
    src: string;
    size?: string;
  }>;

  // Error handling
  fallbackSrc?: string;
  onLoadingComplete?: () => void;
  onError?: () => void;

  // Accessibility enhancements
  role?: string;

  // SEO structured data
  includeImageSchema?: boolean;
  imageSchema?: {
    name?: string;
    description?: string;
    author?: string;
    contentUrl?: string;
    license?: string;
    acquireLicensePage?: string;
    creditText?: string;
    creator?: string;
    copyrightNotice?: string;
  };
}

export const SEOImage = forwardRef<HTMLImageElement | null, SEOImageProps>(
  (
    {
      src,
      alt,
      title,
      fetchPriority = "auto",
      decoding = "async",
      // Default MUST stay "eager" — HeroUI's @heroui/image (2.2.19 as of
      // 2026-05) has a load-state bug with lazy loading. `useImage` creates
      // a *detached* `new Image()` preloader and sets `img.loading = "lazy"`
      // on it. Detached images never enter the viewport, so the browser
      // never fires `onload` on the preloader. HeroUI's internal status
      // stays "loading" forever → its skeleton overlay never disappears
      // and our `setIsLoaded(true)` (gated on the wrapped onLoad) never
      // fires either, so the opacity-0 → 100 transition never runs. Net
      // effect: images spin/blank forever on home, blogs, etc.
      // See node_modules/@heroui/use-image/dist/index.mjs:30-46.
      // Callers that genuinely need lazy loading (e.g. far-below-fold
      // blog cards) can opt in via `loading="lazy"` ONLY after either
      // (a) HeroUI fixes the bug upstream, or (b) the caller disables
      // HeroUI's skeleton by passing `disableSkeleton`. Don't change this
      // default again without confirming HeroUI 2.2.20+ resolves it.
      loading = "eager",
      sizes,
      srcSet,
      webpSrc,
      avifSrc,
      _placeholderSrc,
      trackLoading = false,
      trackError = false,
      trackingCategory,
      responsive = true,
      breakpoints,
      fallbackSrc,
      onLoadingComplete,
      onError,
      role,
      includeImageSchema = false,
      imageSchema,
      className,
      ...props
    },
    ref
  ) => {
    const tSEO = useTranslations("SEO");
    const [hasError, setHasError] = useState(false);
    // Start visible. The opacity-0 → opacity-100 fade was gated on the load
    // event, but a CACHED image can finish loading before React hydrates and
    // attaches onLoad — leaving the image stuck at opacity-0 (invisible). This
    // hit the eager, high-priority hero (the LCP element) most. Defaulting to
    // loaded keeps the image reliably visible; handleLoad still fires for
    // analytics/onLoadingComplete.
    const [isLoaded, setIsLoaded] = useState(true);
    const [currentSrc, setCurrentSrc] = useState(src);

    // Generate responsive srcSet if breakpoints provided
    const generateSrcSet = () => {
      if (srcSet) return srcSet;

      if (breakpoints && breakpoints.length > 0) {
        return breakpoints.map(bp => `${bp.src} ${bp.maxWidth}w`).join(", ");
      }

      return undefined;
    };

    // Generate sizes attribute
    const generateSizes = () => {
      if (sizes) return sizes;

      if (breakpoints && breakpoints.length > 0) {
        return breakpoints
          .map(bp =>
            bp.size
              ? `(max-width: ${bp.maxWidth}px) ${bp.size}`
              : `${bp.maxWidth}px`
          )
          .join(", ");
      }

      return responsive
        ? "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        : undefined;
    };

    // Handle image load
    const handleLoad = () => {
      setIsLoaded(true);
      onLoadingComplete?.();

      if (trackLoading && typeof window !== "undefined" && "gtag" in window) {
        window.gtag("event", "image_load", {
          event_category: trackingCategory || tSEO("analytics.images"),
          event_label: src,
          value: 1,
        });
      }
    };

    // Handle image error
    const handleError = () => {
      setHasError(true);
      onError?.();

      // Try fallback source
      if (fallbackSrc && currentSrc !== fallbackSrc) {
        setCurrentSrc(fallbackSrc);
        setHasError(false);

        return;
      }

      if (trackError && typeof window !== "undefined" && "gtag" in window) {
        window.gtag("event", "image_error", {
          event_category: trackingCategory || tSEO("analytics.images"),
          event_label: src,
          value: 1,
        });
      }
    };

    // Modern image format support detection and implementation
    useEffect(() => {
      const checkWebPSupport = () => {
        return new Promise(resolve => {
          const webP = new Image();

          webP.onload = webP.onerror = () => {
            resolve(webP.height === 2);
          };
          webP.src =
            "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
        });
      };

      const checkAVIFSupport = () => {
        return new Promise(resolve => {
          const avif = new Image();

          avif.onload = avif.onerror = () => {
            resolve(avif.height === 2);
          };
          avif.src =
            "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=";
        });
      };

      // Use modern formats if available and provided
      const selectBestSrc = async () => {
        if (avifSrc && (await checkAVIFSupport())) {
          setCurrentSrc(avifSrc);
        } else if (webpSrc && (await checkWebPSupport())) {
          setCurrentSrc(webpSrc);
        }
      };

      if (webpSrc || avifSrc) {
        selectBestSrc();
      }
    }, [webpSrc, avifSrc]);

    // Generate structured data for image
    const generateImageSchema = () => {
      if (!includeImageSchema) return null;

      const schema = {
        "@context": "https://schema.org",
        "@type": "ImageObject",
        contentUrl: currentSrc,
        url: currentSrc,
        name: imageSchema?.name || alt,
        description: imageSchema?.description || alt,
        ...imageSchema,
      };

      return (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
          type="application/ld+json"
        />
      );
    };

    if (hasError && !fallbackSrc) {
      return (
        <div
          aria-label={alt}
          className={`bg-default-100 flex items-center justify-center text-default-500 ${className}`}
          role="img"
          style={{
            width: props.width || "100%",
            height: props.height || "200px",
          }}
        >
          <span className="text-sm">{tSEO("image.notAvailable")}</span>
        </div>
      );
    }

    return (
      <>
        {generateImageSchema()}
        <HeroUIImage
          ref={ref}
          alt={alt}
          className={`transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          } ${className}`}
          decoding={decoding}
          fetchPriority={fetchPriority}
          loading={loading}
          role={role}
          sizes={generateSizes()}
          src={currentSrc}
          srcSet={generateSrcSet()}
          title={title}
          onError={handleError}
          onLoad={handleLoad}
          {...props}
        />
      </>
    );
  }
);

SEOImage.displayName = "SEOImage";

// Specialized image components

export interface HeroImageProps {
  alt: string;
  src: string;
}

export function HeroImage({ alt, src }: HeroImageProps) {
  return (
    <div className="lg:w-1/2 w-full flex items-center justify-center lg:order-2 order-1 lg:pl-8">
      <div className="w-full max-w-2xl rounded-[2rem] overflow-hidden bg-[#FCFDFF]">
        {/* next/image with real width/height + `w-full h-auto`: responsive at
            the illustration's natural aspect, reliably visible (no opacity
            gate), AND a real intrinsic width so the flex column has something
            to size against — `fill` has no intrinsic width and collapsed the
            two-column hero. */}
        {/* Served as a pre-sized static webp via `unoptimized`, so it skips
            the Vercel /_next/image optimizer. That optimizer is a serverless
            function whose cold start was the last thing keeping mobile LCP
            high on synthetic tests; a static asset is CDN-cached and instant.
            `priority` still emits the preload + fetchpriority=high. The source
            webp is already sized for the slot (1200px wide, ~70 KB), so no
            meaningful responsive resizing is lost for this one hero image. */}
        <NextImage
          priority
          unoptimized
          alt={alt}
          className="w-full h-auto"
          height={1600}
          src={src}
          width={1200}
        />
      </div>
    </div>
  );
}

export interface ResponsiveImageProps extends SEOImageProps {
  aspectRatio?: "square" | "video" | "photo" | "wide";
}

export function ResponsiveImage({
  aspectRatio = "photo",
  className,
  ...props
}: ResponsiveImageProps) {
  const aspectClasses = {
    square: "aspect-square",
    video: "aspect-video",
    photo: "aspect-[4/3]",
    wide: "aspect-[16/9]",
  };

  return (
    <div className={`relative ${aspectClasses[aspectRatio]} ${className}`}>
      <SEOImage
        className="w-full h-full object-cover"
        responsive={true}
        {...props}
      />
    </div>
  );
}

export interface LazyImageProps extends SEOImageProps {
  placeholder?: React.ReactNode;
  threshold?: number;
}

export function LazyImage({
  placeholder,
  threshold = 0.1,
  className,
  ...props
}: LazyImageProps) {
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    const element = document.getElementById(`lazy-${props.src}`);

    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [props.src, threshold]);

  return (
    <div className={className} id={`lazy-${props.src}`}>
      {isInView ? (
        <SEOImage {...props} />
      ) : (
        placeholder || (
          <div className="bg-default-100 animate-pulse w-full h-full" />
        )
      )}
    </div>
  );
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}
